import * as React from 'react'
import { AlertTriangle, Cpu } from 'lucide-react'
import { useGetPlcSyncEligibleQuery } from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../../../components/ui/badge'
import { Checkbox } from '../../../components/ui/checkbox'
import { cn } from '../../../lib/utils'
import { resolveSiteId } from './siteIds'
import { WizardChrome } from './WizardChrome'
import type { IngestWizardApi } from './useIngestWizard'

interface StepUpdatePlcProps {
  wizard: IngestWizardApi
}

export function StepUpdatePlc({ wizard }: StepUpdatePlcProps) {
  const { state, siteName, startPlcUpdate, backToPick } = wizard
  const siteId = resolveSiteId({
    mode: state.mode,
    existingSiteId: state.existingSiteId,
    newSiteName: state.newSiteName || siteName
  })

  const { data, isFetching, isError, error, refetch } = useGetPlcSyncEligibleQuery(
    { siteId },
    { skip: !siteId }
  )

  const [selected, setSelected] = React.useState<Set<string>>(new Set())

  const eligible = data?.eligible ?? []
  const ineligible = data?.ineligible ?? []

  const toggle = (linkId: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(linkId)) next.delete(linkId)
      else next.add(linkId)
      return next
    })

  const allSelected = eligible.length > 0 && selected.size === eligible.length
  const toggleAll = () =>
    setSelected(() => (allSelected ? new Set() : new Set(eligible.map((c) => c.devops_link_id))))

  const onContinue = () => {
    const items = eligible
      .filter((controller) => selected.has(controller.devops_link_id))
      .map((controller) => ({ devops_link_id: controller.devops_link_id }))
    if (items.length === 0) return
    startPlcUpdate(items)
  }

  const errorMessage =
    isError && error && typeof error === 'object' && 'data' in error
      ? extractErrorText((error as { data?: unknown }).data)
      : isError
        ? 'Failed to load eligible controllers.'
        : null

  return (
    <WizardChrome
      eyebrow="Step 03 — Update PLC"
      title={`Pull the latest PLC code into ${siteName || 'this site'}`}
      description="Select the mapped controllers to re-sync from their DevOps repository. Each pulls its latest export from the machine and stages a graph diff — no file upload needed."
      onBack={backToPick}
      onContinue={onContinue}
      continueLabel={
        selected.size > 0
          ? `Update ${selected.size} controller${selected.size > 1 ? 's' : ''}`
          : 'Update'
      }
      continueDisabled={selected.size === 0}
    >
      {isFetching ? (
        <p className="py-6 text-center text-sm text-ink-3">Loading eligible controllers…</p>
      ) : errorMessage ? (
        <div className="flex flex-col items-start gap-2 rounded-lg border border-st-down-bg bg-st-down-bg/30 p-3 text-sm text-st-down">
          <span>{errorMessage}</span>
          <button
            type="button"
            onClick={() => void refetch()}
            className="text-xs font-medium underline underline-offset-2"
          >
            Retry
          </button>
        </div>
      ) : eligible.length === 0 && ineligible.length === 0 ? (
        <p className="py-6 text-center text-sm text-ink-3">
          No PLC controllers are mapped to a DevOps repository for this site yet.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {eligible.length > 0 ? (
            <div className="flex flex-col gap-1.5">
              <button
                type="button"
                onClick={toggleAll}
                className="self-start text-11 font-medium tracking-[0.08em] text-ink-3 uppercase hover:text-brand"
              >
                {allSelected ? 'Clear all' : 'Select all'}
              </button>
              {eligible.map((controller) => {
                const checked = selected.has(controller.devops_link_id)
                return (
                  <label
                    key={controller.devops_link_id}
                    className={cn(
                      'flex cursor-pointer items-start gap-3 rounded-lg border px-3.5 py-3 transition-colors duration-200 motion-reduce:transition-none',
                      checked
                        ? 'border-brand/40 bg-brand-soft/20'
                        : 'border-line bg-bg-elev hover:bg-bg-sunk/40'
                    )}
                  >
                    <Checkbox
                      className="mt-0.5"
                      checked={checked}
                      onCheckedChange={() => toggle(controller.devops_link_id)}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <Cpu className="size-3.5 shrink-0 text-ink-3" />
                        <span className="truncate text-14 font-medium text-ink">
                          {controller.controller_name}
                        </span>
                        <Badge
                          variant="secondary"
                          className="border-transparent bg-st-idle-bg font-mono text-10 text-st-idle"
                        >
                          {controller.resource_type || 'plc'}
                        </Badge>
                      </span>
                      <span className="mt-1 block truncate font-mono text-xs text-ink-3">
                        {controller.local_file}
                      </span>
                    </span>
                  </label>
                )
              })}
            </div>
          ) : null}

          {ineligible.length > 0 ? (
            <div className="flex flex-col gap-1.5">
              <span className="text-11 font-medium tracking-[0.08em] text-ink-3 uppercase">
                Needs a re-ingest first
              </span>
              {ineligible.map((controller) => (
                <div
                  key={controller.devops_link_id}
                  className="flex items-start gap-3 rounded-lg border border-line bg-bg-sunk/40 px-3.5 py-3 opacity-70"
                >
                  <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-st-warn" />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="truncate text-14 font-medium text-ink-3">
                        {controller.controller_name}
                      </span>
                      <Badge
                        variant="secondary"
                        className="border-transparent bg-st-idle-bg font-mono text-10 text-st-idle"
                      >
                        {controller.resource_type || 'plc'}
                      </Badge>
                    </span>
                    <span className="mt-1 block text-xs leading-snug text-ink-3">
                      {controller.reason}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      )}
    </WizardChrome>
  )
}

function extractErrorText(data: unknown): string {
  if (data && typeof data === 'object' && 'error' in data) {
    const message = (data as { error?: unknown }).error
    if (typeof message === 'string' && message.trim()) return message
  }
  return 'Failed to load eligible controllers.'
}
